// Mirrors the lesson markdown parsing + rendering used in PracticalAIApp/src/App.js
// so the admin preview matches what users actually see in the app.

import React from 'react';
import MarkdownIt from 'markdown-it';
import taskLists from 'markdown-it-task-lists';
import markdownItContainer from 'markdown-it-container';
import footnote from 'markdown-it-footnote';

// RichText component matching PracticalAIApp - renders inline markdown
export function RichText({ text, style = {} }) {
  if (!text) return null;
  const lines = String(text).split("\n");
  return (
    <span style={style}>
      {lines.map((line, i) => (
        <span key={i}>
          {renderInline(line)}
          {i < lines.length - 1 && <br />}
        </span>
      ))}
    </span>
  );
}

function renderInline(text) {
  const parts = [];
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|(https?:\/\/[^\s]+))/g;
  let last = 0, m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[2]) parts.push(<strong key={m.index} style={{color:"#fff",fontWeight:700}}>{m[2]}</strong>);
    else if (m[3]) parts.push(<em key={m.index} style={{fontStyle:"italic",opacity:0.9}}>{m[3]}</em>);
    else if (m[4]) parts.push(<code key={m.index} style={{background:"rgba(43,124,233,0.2)",color:"#5B9EF0",padding:"1px 5px",borderRadius:4,fontSize:"0.9em",fontFamily:"monospace"}}>{m[4]}</code>);
    else if (m[5]) parts.push(<a key={m.index} href={m[5]} target="_blank" rel="noopener noreferrer" style={{color:"#5B9EF0",textDecoration:"underline",wordBreak:"break-all"}}>{m[5]}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
}

const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
  breaks: true
}).enable(['table'])
.use(taskLists, { disabled: false })
.use(footnote)
.use(markdownItContainer, 'tip', {
  validate: (params) => /^tip\b/.test(params.trim()),
  render: (tokens, idx) => tokens[idx].nesting === 1 ? '<div style="margin-top:12px;margin-bottom:12px;background:rgba(52,211,153,0.08);border:1px solid rgba(52,211,153,0.3);border-radius:12px;padding:12px 16px;display:flex;gap:10px;align-items:flex-start"><div style="font-size:16px;flex-shrink:0">💡</div><div style="font-size:15px;color:#34D399;line-height:1.6">' : '</div></div>'
})
.use(markdownItContainer, 'warning', {
  validate: (params) => /^warning\b/.test(params.trim()),
  render: (tokens, idx) => tokens[idx].nesting === 1 ? '<div style="margin-top:12px;margin-bottom:12px;background:rgba(240,165,0,0.08);border:1px solid rgba(240,165,0,0.3);border-radius:12px;padding:12px 16px;display:flex;gap:10px;align-items:flex-start"><div style="font-size:16px;flex-shrink:0">!</div><div style="font-size:15px;color:#F0A500;line-height:1.6">' : '</div></div>'
})
.use(markdownItContainer, 'info', {
  validate: (params) => /^info\b/.test(params.trim()),
  render: (tokens, idx) => tokens[idx].nesting === 1 ? '<div style="margin-top:12px;margin-bottom:12px;background:rgba(43,124,233,0.08);border:1px solid rgba(43,124,233,0.27);border-radius:12px;padding:12px 16px;display:flex;gap:10px;align-items:flex-start"><div style="font-size:16px;flex-shrink:0">i</div><div style="font-size:15px;color:#5B9EF0;line-height:1.6">' : '</div></div>'
})
.use(markdownItContainer, 'success', {
  validate: (params) => /^success\b/.test(params.trim()),
  render: (tokens, idx) => tokens[idx].nesting === 1 ? '<div style="margin-top:12px;margin-bottom:12px;background:rgba(52,211,153,0.08);border:1px solid rgba(52,211,153,0.3);border-radius:12px;padding:12px 16px;display:flex;gap:10px;align-items:flex-start"><div style="font-size:16px;flex-shrink:0">ok!</div><div style="font-size:15px;color:#34D399;line-height:1.6">' : '</div></div>'
})
.use(markdownItContainer, 'error', {
  validate: (params) => /^error\b/.test(params.trim()),
  render: (tokens, idx) => tokens[idx].nesting === 1 ? '<div style="margin-top:12px;margin-bottom:12px;background:rgba(248,113,113,0.08);border:1px solid rgba(248,113,113,0.3);border-radius:12px;padding:12px 16px;display:flex;gap:10px;align-items:flex-start"><div style="font-size:16px;flex-shrink:0">❌</div><div style="font-size:15px;color:#F87171;line-height:1.6">' : '</div></div>'
})
.use(markdownItContainer, 'question', {
  validate: (params) => /^question\b/.test(params.trim()),
  render: (tokens, idx) => tokens[idx].nesting === 1 ? '<div style="margin-top:12px;margin-bottom:12px;background:rgba(245,158,11,0.1);border:1px solid rgba(245,158,11,0.3);border-radius:12px;padding:12px 16px;display:flex;gap:10px;align-items:flex-start"><div style="width:20px;height:20px;flex-shrink:0;background:#F59E0B;border-radius:4px;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px">?</div><div style="font-size:15px;color:#F59E0B;line-height:1.6">' : '</div></div>'
});

// Create a secondary markdown instance for parsing content inside fences
const mdInline = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
  breaks: true
}).enable(['table'])
.use(taskLists, { disabled: false })
.use(markdownItContainer, 'tip', {
  validate: (params) => /^tip\b/.test(params.trim()),
  render: (tokens, idx) => tokens[idx].nesting === 1 ? '<div style="margin-top:12px;margin-bottom:12px;background:rgba(52,211,153,0.08);border:1px solid rgba(52,211,153,0.3);border-radius:12px;padding:12px 16px;display:flex;gap:10px;align-items:flex-start"><div style="font-size:16px;flex-shrink:0">💡</div><div style="font-size:15px;color:#34D399;line-height:1.6">' : '</div></div>'
})
.use(markdownItContainer, 'warning', {
  validate: (params) => /^warning\b/.test(params.trim()),
  render: (tokens, idx) => tokens[idx].nesting === 1 ? '<div style="margin-top:12px;margin-bottom:12px;background:rgba(240,165,0,0.08);border:1px solid rgba(240,165,0,0.3);border-radius:12px;padding:12px 16px;display:flex;gap:10px;align-items:flex-start"><div style="font-size:16px;flex-shrink:0">!</div><div style="font-size:15px;color:#F0A500;line-height:1.6">' : '</div></div>'
})
.use(markdownItContainer, 'info', {
  validate: (params) => /^info\b/.test(params.trim()),
  render: (tokens, idx) => tokens[idx].nesting === 1 ? '<div style="margin-top:12px;margin-bottom:12px;background:rgba(43,124,233,0.08);border:1px solid rgba(43,124,233,0.27);border-radius:12px;padding:12px 16px;display:flex;gap:10px;align-items:flex-start"><div style="font-size:16px;flex-shrink:0">i</div><div style="font-size:15px;color:#5B9EF0;line-height:1.6">' : '</div></div>'
})
.use(markdownItContainer, 'success', {
  validate: (params) => /^success\b/.test(params.trim()),
  render: (tokens, idx) => tokens[idx].nesting === 1 ? '<div style="margin-top:12px;margin-bottom:12px;background:rgba(52,211,153,0.08);border:1px solid rgba(52,211,153,0.3);border-radius:12px;padding:12px 16px;display:flex;gap:10px;align-items:flex-start"><div style="font-size:16px;flex-shrink:0">ok!</div><div style="font-size:15px;color:#34D399;line-height:1.6">' : '</div></div>'
})
.use(markdownItContainer, 'error', {
  validate: (params) => /^error\b/.test(params.trim()),
  render: (tokens, idx) => tokens[idx].nesting === 1 ? '<div style="margin-top:12px;margin-bottom:12px;background:rgba(248,113,113,0.08);border:1px solid rgba(248,113,113,0.3);border-radius:12px;padding:12px 16px;display:flex;gap:10px;align-items:flex-start"><div style="font-size:16px;flex-shrink:0">❌</div><div style="font-size:15px;color:#F87171;line-height:1.6">' : '</div></div>'
})
.use(markdownItContainer, 'question', {
  validate: (params) => /^question\b/.test(params.trim()),
  render: (tokens, idx) => tokens[idx].nesting === 1 ? '<div style="margin-top:12px;margin-bottom:12px;background:rgba(245,158,11,0.1);border:1px solid rgba(245,158,11,0.3);border-radius:12px;padding:12px 16px;display:flex;gap:10px;align-items:flex-start"><div style="width:20px;height:20px;flex-shrink:0;background:#F59E0B;border-radius:4px;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px">?</div><div style="font-size:15px;color:#F59E0B;line-height:1.6">' : '</div></div>'
});

// Helper function to copy rules after they're all set up
function copyRulesToInlineParser() {
  Object.keys(md.renderer.rules).forEach(rule => {
    mdInline.renderer.rules[rule] = md.renderer.rules[rule];
  });
}

// Container plugins handle the fences - no preprocessing needed

// Style bullet lists
md.renderer.rules.bullet_list_open = () => `<ul style='padding-left: 48px; margin: 12px 0; list-style-type: disc;'>`;
md.renderer.rules.list_item_open = function(tokens, idx) {
  const token = tokens[idx];
  if (token.attrGet('class') === 'task-list-item') {
    return `<li style='color: #7A9CC4; line-height: 1.6; margin-bottom: 8px; list-style-type: none; display: flex; align-items: center; gap: 8px;'>`;
  }
  return `<li style='color: #7A9CC4; line-height: 1.6; margin-bottom: 8px; list-style-type: disc;'>`;
};

// Style headings
md.renderer.rules.heading_open = (tokens, idx) => {
  const level = tokens[idx].tag;
  const sizes = { h1: '28px', h2: '20px', h3: '16px' };
  if (level === 'h2') {
    return `<div style='margin-top: 12px;'><${level} style='color: #5B9EF0; margin: 0 0 12px 0; font-size: ${sizes[level] || '14px'}; font-weight: 700;'>`;
  }
  if (level === 'h3') {
    return `<div style='margin-top: 8px;'><${level} style='color: #5B9EF0; margin: 0 0 12px 0; font-size: ${sizes[level] || '14px'}; font-weight: 700;'>`;
  }
  return `<${level} style='color: #5B9EF0; margin: 0 0 12px 0; font-size: ${sizes[level] || '14px'};'>`;
};

// Close heading wrapper for h2 and h3
md.renderer.rules.heading_close = (tokens, idx) => {
  const level = tokens[idx].tag;
  if (level === 'h2' || level === 'h3') {
    return `</${level}></div>`;
  }
  return `</${level}>`;
};

// Custom rendering for dark theme
md.renderer.rules.table_open = () => `<table style='width:100%; border-collapse:collapse; margin:16px 0; border:1px solid rgba(59,130,246,0.3); background:rgba(59,130,246,0.05);'>`;
md.renderer.rules.thead_open = () => `<thead style='background:rgba(59,130,246,0.15);'>`;
md.renderer.rules.th_open = () => `<th style='padding:12px; text-align:left; color:#5B9EF0; border-right:1px solid rgba(59,130,246,0.2); font-weight:600; border-bottom:2px solid rgba(59,130,246,0.3);'>`;
md.renderer.rules.tbody_open = () => `<tbody>`;
md.renderer.rules.tr_open = (tokens, idx) => {
  const rowIdx = idx - 2;
  const isEven = rowIdx % 2 === 0;
  return `<tr style='border-bottom:1px solid rgba(59,130,246,0.2); background:${isEven ? 'transparent' : 'rgba(59,130,246,0.03);'}'>`;
};
md.renderer.rules.td_open = () => `<td style='padding:10px 12px; color:#9DB4D4; border-right:1px solid rgba(59,130,246,0.15);'>`;

// Style horizontal rules
md.renderer.rules.hr = () => `<hr style='margin: 24px 0; border: none; border-top: 2px solid rgba(59, 130, 246, 0.6);'>`;

// Copy all rules to inline parser now that they're all set up
copyRulesToInlineParser();

export const parseMarkdownToBody = (md) => {
  if (!md || typeof md !== 'string') return [];

  const body = [];
  const lines = md.split("\n");
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trimEnd();

    if (!line.trim()) {
      i++;
      continue;
    }

    // Fence syntax: :::blocktype ... ::: (both inline and multiline)
    if (line.startsWith(':::')) {
      let match;
      if (line.endsWith(':::') && line !== ':::') {
        // Inline with closing: :::blocktype content:::
        match = line.match(/^:::([\w-]+)\s+(.+?)\s*:::$/);
      } else {
        // Block start or just :::
        match = line.match(/^:::([\w-]+)(.*)$/);
      }
      if (match) {
        const blockType = match[1].toLowerCase();
        let contentStr = (match[2] || '').trim();
        let summary = contentStr; // For details blocks, summary is on the same line

        // Details blocks always collect multi-line content
        if (blockType === 'details' || !contentStr.trim()) {
          const content = [];
          i++;
          while (i < lines.length && !lines[i].trimEnd().startsWith(':::')) {
            content.push(lines[i]);
            i++;
          }
          i++; // skip closing :::
          const fullContent = content.join('\n').trim();

          if (blockType === 'details') {
            // For details: summary is from first line, content is from remaining lines
            contentStr = fullContent;
          } else {
            contentStr = fullContent;
          }
        } else {
          i++;
        }

        if (blockType === 'tip' || blockType === 'warning' || blockType === 'info' || blockType === 'success' || blockType === 'question') {
          body.push(['alert', `${blockType}||${contentStr}`]);
        } else if (blockType === 'video' || blockType === 'youtube' || blockType === 'yt') {
          body.push(['yt', contentStr]);
        } else if (blockType === 'callout' || blockType === 'note') {
          body.push(['c', contentStr]);
        } else if (blockType === 'cta' || blockType === 'button') {
          body.push(['btn', contentStr.replace(',', '||')]);
        } else if (blockType === 'details') {
          body.push(['details', { summary: summary || 'Details', content: contentStr }]);
        } else {
          body.push(['p', contentStr]);
        }
        continue;
      }
    }

    // Headings
    if (/^# /.test(line)) {
      body.push(["h1", line.substring(2).trim()]);
      i++;
    } else if (/^## /.test(line)) {
      body.push(["h2", line.substring(3).trim()]);
      i++;
    } else if (/^### /.test(line)) {
      body.push(["h3", line.substring(4).trim()]);
      i++;
    } else if (/^[*-] /.test(line)) {
      const items = [];
      while (i < lines.length && /^[*-] /.test(lines[i].trimEnd())) {
        items.push(lines[i].trimEnd().substring(2));
        i++;
      }
      body.push(["ul", items]);
    } else {
      body.push(["p", line.trim()]);
      i++;
    }
  }

  return body;
};

const escapeHtml = (str) =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const renderInlineHtml = (text) => {
  if (!text) return '';
  const input = String(text);
  let html = input;

  // Process bold text **text** - MUST come first - match anything between **
  html = html.replace(/\*\*(.+?)\*\*/g, (match, content) => {
    return `<strong style="color:#fff;font-weight:700">${content}</strong>`;
  });

  // Process italic *text* - match anything between single *
  html = html.replace(/(?<!\*)\*(.+?)\*(?!\*)/g, (match, content) => {
    // Don't process if this is part of a bold tag
    if (match.includes('<strong')) return match;
    return `<em style="font-style:italic;opacity:0.9">${content}</em>`;
  });

  // Process code `text`
  html = html.replace(/`(.+?)`/g, (match, content) => {
    return `<code style="background:rgba(43,124,233,0.2);color:#5B9EF0;padding:1px 5px;border-radius:4px;font-size:0.9em;font-family:monospace">${content}</code>`;
  });

  // Process links
  html = html.replace(/(https?:\/\/[^\s<>]+)/g, (match, url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:#5B9EF0;text-decoration:underline;word-break:break-all">${url}</a>`;
  });

  return html;
};

const renderTextHtml = (text) => {
  const lines = String(text || "").split("\n");
  return lines.map(line => {
    const trimmed = line.trim();
    // Check for checkbox syntax: [ ] or [x]
    const checkboxMatch = trimmed.match(/^\s*\[\s*([xX ]?)\s*\]\s*(.*)/);
    if (checkboxMatch) {
      const checkChar = checkboxMatch[1].trim().toLowerCase();
      const isChecked = checkChar === 'x';
      const content = checkboxMatch[2].trim();
      const htmlContent = renderInlineHtml(content);
      const checkedAttr = isChecked ? 'checked disabled' : 'disabled';
      return `<span style="display: flex; align-items: flex-start; gap: 8px;"><input type="checkbox" ${checkedAttr} style="cursor: pointer; width: 16px; height: 16px; accent-color: #22C55E; margin-top: 2px; flex-shrink: 0; pointer-events: none;" /><span>${htmlContent}</span></span>`;
    }
    return renderInlineHtml(line);
  }).join("<br/>");
};

const renderListItemHtml = (text) => {
  const trimmed = String(text || "").trim();
  // Check for checkbox syntax: [ ], [x], [X], [ ] with various spacing
  const checkboxMatch = trimmed.match(/^\s*\[\s*([xX ]?)\s*\]\s*(.*)/);
  if (checkboxMatch) {
    const checkChar = checkboxMatch[1].trim().toLowerCase();
    const isChecked = checkChar === 'x';
    const content = checkboxMatch[2].trim();
    const htmlContent = content ? renderTextHtml(content) : '';
    const checkedAttr = isChecked ? 'checked disabled' : 'disabled';
    return `<span style="display: flex; align-items: flex-start; gap: 8px;"><input type="checkbox" ${checkedAttr} style="cursor: pointer; width: 16px; height: 16px; accent-color: #22C55E; margin-top: 2px; flex-shrink: 0; pointer-events: none;" /><span>${htmlContent}</span></span>`;
  }
  // Check for bold text at beginning: **text** rest of content
  const boldMatch = trimmed.match(/^\*\*([^*]+)\*\*\s*(.*)/);
  if (boldMatch) {
    const boldText = boldMatch[1];
    const rest = boldMatch[2];
    const restHtml = rest ? renderTextHtml(rest) : '';
    return `<span style="display: block;"><strong style="color:#fff;font-weight:700;font-size:16px">${escapeHtml(boldText)}</strong>${rest ? `<span style="display: block; margin-top: 4px;">${restHtml}</span>` : ''}</span>`;
  }
  return renderTextHtml(trimmed);
};

export const renderLessonBodyHtml = (body) =>
  body
    .map(([type, content]) => {
      switch (type) {
        case "h1":
          return `<h1 style="color:#5B9EF0;margin-top:24px;margin-bottom:12px;font-size:28px;font-weight:700">${renderTextHtml(content)}</h1>`;
        case "h2":
          return `<h2 style="color:#5B9EF0;margin-top:20px;margin-bottom:10px;font-size:20px;font-weight:700">${renderTextHtml(content)}</h2>`;
        case "h3":
        case "h":
          return `<h3 style="color:#5B9EF0;margin-top:16px;margin-bottom:8px;font-size:16px;font-weight:700">${renderTextHtml(content)}</h3>`;
        case "p":
          return `<div style="font-size:15px;line-height:1.75;color:rgba(255,255,255,0.85);margin-bottom:12px">${renderTextHtml(content)}</div>`;
        case "ul": {
          const items = Array.isArray(content) ? content : [content];
          return `<ul style="padding-left:20px;margin-bottom:12px">${items
            .map(
              (item) =>
                `<li style="font-size:15px;line-height:1.75;color:rgba(255,255,255,0.82);margin-bottom:5px">${renderListItemHtml(item)}</li>`
            )
            .join("")}</ul>`;
        }
        case "c":
          return `<div style="background:rgba(240,165,0,0.1);border:1px solid rgba(240,165,0,0.3);border-radius:12px;padding:14px 16px;font-size:15px;color:#F0A500;line-height:1.75;font-style:italic;margin-top:20px;margin-bottom:8px">${renderTextHtml(content)}</div>`;
        case "btn": {
          const [label, url] = String(content).split("||");
          return `<div style="margin-top:16px;margin-bottom:8px"><a href="${escapeHtml(url || "#")}" target="_blank" rel="noopener noreferrer" style="display:block;text-decoration:none"><div style="background:#F0A500;color:#071829;border-radius:12px;padding:14px 20px;text-align:center;font-size:18px;font-weight:700;cursor:pointer">${escapeHtml(label || "Click Here")}</div></a></div>`;
        }
        case "alert": {
          const [alertType, msg] = String(content).split("||");
          const styles = {
            tip: { bg: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.3)", color: "#10B981", icon: "💡" },
            warning: { bg: "rgba(240,165,0,0.08)", border: "1px solid rgba(240,165,0,0.3)", color: "#F0A500", icon: "!" },
            info: { bg: "rgba(43,124,233,0.08)", border: "1px solid rgba(43,124,233,0.3)", color: "#5B9EF0", icon: "i" },
            success: { bg: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.3)", color: "#10B981", icon: "✓" },
          };
          const s = styles[alertType] || styles.info;
          return `<div style="margin-top:12px;margin-bottom:12px;background:${s.bg};border:${s.border};border-radius:12px;padding:12px 16px;display:flex;gap:10px;align-items:flex-start"><div style="font-size:16px;flex-shrink:0">${s.icon}</div><div style="font-size:15px;color:${s.color};line-height:1.6">${renderTextHtml(msg)}</div></div>`;
        }
        case "yt": {
          const url = content || "";
          const match = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
          const vid = match ? match[1] : url.trim().length === 11 ? url.trim() : null;
          if (!vid)
            return `<div style="padding:10px 14px;background:rgba(248,113,113,0.08);border:1px solid rgba(248,113,113,0.3);border-radius:10px;font-size:12px;color:#F87171;margin-bottom:8px">! Invalid YouTube URL: ${escapeHtml(url)}</div>`;
          return `<div style="position:relative;width:100%;padding-bottom:56.25%;height:0;margin:16px 0;border-radius:12px;overflow:hidden;background:#000"><iframe style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;border-radius:12px" src="https://www.youtube.com/embed/${vid}?rel=0" title="Video" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>`;
        }
        default:
          return "";
      }
    })
    .join("\n");

export const buildLessonPreviewHtml = ({ title, subtitle, icon, tag, dur, content }) => {
  const body = parseMarkdownToBody(content);
  const bodyHtml = renderLessonBodyHtml(body);
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${escapeHtml(title || "Lesson Preview")}</title>
<style>
  * { box-sizing: border-box; }
  body { margin:0; background:#071829; color:#fff; font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif; }
  .wrap { max-width:680px; margin:0 auto; padding:32px 20px 80px; }
  .badge { display:inline-block; font-size:12px; color:#7A9CC4; text-transform:uppercase; letter-spacing:0.06em; margin-bottom:10px; }
</style>
</head>
<body>
<div class="wrap">
  <div class="badge">${escapeHtml([tag, dur].filter(Boolean).join(" • ")) || "Preview"}</div>
  <div style="font-size:26px;font-weight:700;display:flex;align-items:center;gap:10px">${icon ? `<span>${escapeHtml(icon)}</span>` : ""}<span>${escapeHtml(title || "Untitled Lesson")}</span></div>
  ${subtitle ? `<div style="font-size:15px;color:#7A9CC4;margin-top:8px">${escapeHtml(subtitle)}</div>` : ""}
  <div style="margin-top:20px;border-top:1px solid rgba(59,130,246,0.15);padding-top:20px">${bodyHtml || '<div style="color:#7A9CC4">No content yet.</div>'}</div>
</div>
</body>
</html>`;
};

export const openLessonPreview = (lesson) => {
  const html = buildLessonPreviewHtml(lesson);
  const win = window.open("", "_blank");
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  return true;
};

export const renderLessonContent = ({ title, subtitle, icon, tag, dur, content }) => {
  const body = parseMarkdownToBody(content);
  const bodyHtml = renderLessonBodyHtml(body);

  return (
    <div style={{ maxWidth: "680px", margin: "0 auto" }}>
      <div style={{ fontSize: "12px", color: "#7A9CC4", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "10px" }}>
        {[tag, dur].filter(Boolean).join(" • ") || "Preview"}
      </div>
      <div style={{ fontSize: "26px", fontWeight: "700", display: "flex", alignItems: "center", gap: "10px" }}>
        {icon && <span>{icon}</span>}
        <span>{title || "Untitled Lesson"}</span>
      </div>
      {subtitle && (
        <div style={{ fontSize: "15px", color: "#7A9CC4", marginTop: "8px" }}>
          {subtitle}
        </div>
      )}
      <div style={{ marginTop: "20px", borderTop: "1px solid rgba(59, 130, 246, 0.15)", paddingTop: "20px" }}>
        {bodyHtml ? (
          <div dangerouslySetInnerHTML={{ __html: bodyHtml }} style={{ color: "#7A9CC4", lineHeight: "1.6" }} />
        ) : (
          <div style={{ color: "#7A9CC4" }}>No content yet.</div>
        )}
      </div>
    </div>
  );
};

export const renderLessonContentWithMarkdown = ({ title, subtitle, icon, tag, dur, body }) => {
  // If body is raw markdown string, render it directly with md instance for full footnote support
  if (typeof body === 'string' && body.trim()) {
    const bodyHtml = md.render(body);
    const styles = `<style>
    h1, h2, h3, h4, h5, h6 { padding-top: 0; padding-bottom: 0; }
    ul, ol { padding-left: 32px; margin: 8px 0; }
    li { margin-bottom: 6px; }
    sup.footnote-ref { font-size: 0.8em; vertical-align: super; }
    sup.footnote-ref a { color: #5B9EF0; text-decoration: none; }
    sup.footnote-ref a:hover { text-decoration: underline; }
    .footnotes { margin-top: 32px; padding-top: 16px; border-top: 1px solid rgba(59,130,246,0.2); font-size: 13px; }
    .footnotes ol { padding-left: 20px; }
    .footnotes li { margin-bottom: 8px; line-height: 1.6; color: #9DB4D4; }
    .footnotes a { color: #5B9EF0; text-decoration: none; }
    .footnotes a:hover { text-decoration: underline; }
  </style>`;
    return (
      <div style={{ maxWidth: "680px", margin: "0 auto" }}>
        <div style={{ background: "rgba(59, 130, 246, 0.08)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "12px", padding: "32px 24px", marginBottom: "32px", textAlign: "center" }}>
          <div style={{ fontSize: "12px", color: "#7A9CC4", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "16px", fontWeight: "600" }}>
            {dur || "Preview"}
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", marginBottom: "16px" }}>
            {title || "Untitled Lesson"}
          </div>
          {subtitle && (
            <div style={{ fontSize: "14px", color: "#9DB4D4", lineHeight: "1.6" }}>
              {subtitle}
            </div>
          )}
        </div>
        <div style={{ marginTop: "0" }}>
          <div dangerouslySetInnerHTML={{ __html: styles + bodyHtml }} style={{ color: "#7A9CC4", lineHeight: "1.6" }} />
        </div>
      </div>
    );
  }

  const renderBodyContent = (bodyArray) => {
    if (!Array.isArray(bodyArray) || bodyArray.length === 0) {
      return '<div style="color:#7A9CC4">No content yet.</div>';
    }

    return bodyArray.map(([type, content], i) => {
      if (type === "md") {
        return `<div key="${i}" style="margin-bottom:8px">${md.render(content || "")}</div>`;
      } else if (type === "h1" || type === "h") {
        return `<h1 key="${i}" style="color:#5B9EF0;margin-top:12px;margin-bottom:6px;font-size:28px;font-weight:700"><div style="color: rgba(255,255,255,0.85);">${renderTextHtml(content || "")}</div></h1>`;
      } else if (type === "h2") {
        return `<h2 key="${i}" style="color:#5B9EF0;margin-top:12px;margin-bottom:6px;font-size:20px;font-weight:700"><div style="color: rgba(255,255,255,0.85);">${renderTextHtml(content || "")}</div></h2>`;
      } else if (type === "h3") {
        return `<h3 key="${i}" style="color:#5B9EF0;margin-top:12px;margin-bottom:6px;font-size:16px;font-weight:700"><div style="color: rgba(255,255,255,0.85);">${renderTextHtml(content || "")}</div></h3>`;
      } else if (type === "p") {
        return `<div key="${i}" style="font-size:15px;line-height:1.6;color:rgba(255,255,255,0.85);margin-bottom:12px">${renderTextHtml(content || "")}</div>`;
      } else if (type === "ul") {
        const items = Array.isArray(content) ? content : [content];
        return `<ul key="${i}" style="padding-left:32px;margin:8px 0;color:rgba(255,255,255,0.85)">${items.map(item => `<li style="margin-bottom:6px">${renderTextHtml(item || "")}</li>`).join('')}</ul>`;
      } else if (type === "c") {
        return `<div key="${i}" style="background:rgba(240,165,0,0.1);border:1px solid rgba(240,165,0,0.3);border-radius:12px;padding:14px 16px;font-size:15px;color:#F0A500;line-height:1.6;font-style:italic;margin-top:12px;margin-bottom:8px">${renderTextHtml(content || "")}</div>`;
      } else if (type === "alert") {
        const [alertType, msg] = String(content).split("||");
        const styles = {
          tip: { bg: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.3)", color: "#10B981", icon: "💡" },
          warning: { bg: "rgba(240,165,0,0.08)", border: "1px solid rgba(240,165,0,0.3)", color: "#F0A500", icon: "!" },
          info: { bg: "rgba(43,124,233,0.08)", border: "1px solid rgba(43,124,233,0.3)", color: "#5B9EF0", icon: "i" },
          success: { bg: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.3)", color: "#10B981", icon: "✓" },
          question: { bg: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", color: "#F59E0B", icon: "?" },
        };
        const s = styles[alertType] || styles.info;
        return `<div key="${i}" style="margin-top:12px;margin-bottom:12px;background:${s.bg};border:${s.border};border-radius:12px;padding:12px 16px;display:flex;gap:10px;align-items:flex-start"><div style="font-size:16px;flex-shrink:0">${s.icon}</div><div style="font-size:15px;color:${s.color};line-height:1.6">${renderTextHtml(msg || "")}</div></div>`;
      } else if (type === "btn") {
        const [label, url] = String(content).split("||");
        return `<div key="${i}" style="margin-top:16px;margin-bottom:8px"><a href="${escapeHtml(url || "#")}" target="_blank" rel="noopener noreferrer" style="display:block;text-decoration:none"><div style="background:#F0A500;color:#071829;border-radius:12px;padding:14px 20px;text-align:center;font-size:18px;font-weight:700;cursor:pointer">${escapeHtml(label || "Click Here")}</div></a></div>`;
      } else if (type === "yt") {
        const url = content || "";
        const match = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
        const vid = match ? match[1] : url.trim().length === 11 ? url.trim() : null;
        if (!vid) {
          return `<div key="${i}" style="padding:10px 14px;background:rgba(248,113,113,0.08);border:1px solid rgba(248,113,113,0.3);border-radius:10px;font-size:12px;color:#F87171;margin-bottom:8px">! Invalid YouTube URL: ${escapeHtml(url)}</div>`;
        }
        return `<div key="${i}" style="position:relative;width:100%;padding-bottom:56.25%;height:0;margin:16px 0;border-radius:12px;overflow:hidden;background:#000"><iframe style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;border-radius:12px" src="https://www.youtube.com/embed/${vid}?rel=0" title="Video" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>`;
      } else if (type === "details") {
        const summary = content?.summary || 'Details';
        const detailsContent = content?.content || content || '';
        return `<details key="${i}" style="margin:12px 0 32px 0;padding:12px;background:rgba(59,130,246,0.05);border:1px solid rgba(59,130,246,0.2);border-radius:8px"><summary style="cursor:pointer;color:#5B9EF0;font-weight:600;padding:8px 0">${summary}</summary><div style="margin-top:12px;color:rgba(255,255,255,0.85)">${detailsContent}</div></details>`;
      } else {
        return '';
      }
    }).join('');
  };

  const parsedBody = typeof body === 'string' ? parseMarkdownToBody(body) : body;
  const bodyHtml = renderBodyContent(parsedBody);
  const styles = `<style>
    h1, h2, h3, h4, h5, h6 { padding-top: 0; padding-bottom: 0; }
    ul, ol { padding-left: 32px; margin: 8px 0; }
    li { margin-bottom: 6px; }
    sup.footnote-ref { font-size: 0.8em; vertical-align: super; }
    sup.footnote-ref a { color: #5B9EF0; text-decoration: none; }
    sup.footnote-ref a:hover { text-decoration: underline; }
    .footnotes { margin-top: 32px; padding-top: 16px; border-top: 1px solid rgba(59,130,246,0.2); font-size: 13px; }
    .footnotes ol { padding-left: 20px; }
    .footnotes li { margin-bottom: 8px; line-height: 1.6; color: #9DB4D4; }
    .footnotes a { color: #5B9EF0; text-decoration: none; }
    .footnotes a:hover { text-decoration: underline; }
  </style>`;

  return (
    <div style={{ maxWidth: "680px", margin: "0 auto" }}>
      <div style={{ background: "rgba(59, 130, 246, 0.08)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "12px", padding: "32px 24px", marginBottom: "32px", textAlign: "center" }}>
        <div style={{ fontSize: "12px", color: "#7A9CC4", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "16px", fontWeight: "600" }}>
          {dur || "Preview"}
        </div>
        <div style={{ fontSize: "28px", fontWeight: "700", marginBottom: "16px" }}>
          {title || "Untitled Lesson"}
        </div>
        {subtitle && (
          <div style={{ fontSize: "14px", color: "#9DB4D4", lineHeight: "1.6" }}>
            {subtitle}
          </div>
        )}
      </div>
      <div style={{ marginTop: "0" }}>
        <div dangerouslySetInnerHTML={{ __html: styles + bodyHtml }} style={{ color: "#7A9CC4", lineHeight: "1.6" }} />
      </div>
    </div>
  );
};
